import useSWR, { mutate as globalMutate } from 'swr';
import { getContacts, getAllContacts } from '../services/contactServices';

export function getContactsCacheKey(leadId, search) {
  if (leadId === false) return null;
  let processedSearch = search;
  if (search && /^[+\d\s()-]+$/.test(search) && /\d/.test(search)) {
    processedSearch = search.replace(/\s+/g, "");
  }
  return ['contacts', leadId ? String(leadId) : leadId, processedSearch];
}

export function invalidateContactsCache(leadId) {
  return globalMutate(
    key => Array.isArray(key) && key[0] === 'contacts' && (!leadId || String(key[1]) === String(leadId)),
    undefined,
    { revalidate: true }
  );
}

export function useContacts(leadId = null, search = "") {
  const key = getContactsCacheKey(leadId, search);
  
  const { data, error, isLoading, mutate } = useSWR(
    key,
    async () => {
      const result = await getContacts(leadId, search);
      if (result?.success) {
        return result.data || [];
      }
      throw new Error(result?.error || 'Failed to fetch contacts');
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
      dedupingInterval: 2000,
      keepPreviousData: true,
    }
  );

  const refresh = () => invalidateContactsCache(leadId);

  return {
    contacts: data || [],
    isLoading,
    error,
    mutate,
    refresh,
  };
}

export function useAllContacts(pageNumber = 1, pageSize = 10, search = "", sortBy = "", sortOrder = "") {
  let processedSearch = search;
  if (search && /^[+\d\s()-]+$/.test(search) && /\d/.test(search)) {
    processedSearch = search.replace(/\s+/g, "");
  }
  const key = ['all-contacts', pageNumber, pageSize, processedSearch, sortBy, sortOrder];
  
  const { data, error, isLoading, mutate } = useSWR(
    key,
    async () => {
      const result = await getAllContacts(pageNumber, pageSize, processedSearch, sortBy, sortOrder);
      if (result?.success) {
        return {
          contacts: result.data || [],
          totalRecords: result.totalRecords || 0
        };
      }
      throw new Error(result?.error || 'Failed to fetch contacts');
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
      dedupingInterval: 2000,
      keepPreviousData: true,
    }
  );

  const refresh = () => globalMutate(key);

  return {
    contacts: data?.contacts || [],
    totalRecords: data?.totalRecords || 0,
    isLoading,
    error,
    mutate,
    refresh,
  };
}
